import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { getApiUrl, apiRequest } from '../config/api';
import { AVISO_VENTANA_CERRADA, cierreVentanaWhatsApp } from '../utils/ventanaWhatsApp';
import usePegadoAbajo from './usePegadoAbajo';
import useVentanaCerrada from './useVentanaCerrada';
import { ordenarMensajes } from '../utils/ordenMensajes';
import { CODIGOS, codigoDelError, motivoDelEnvio, textoDelError } from '../utils/envioMedia';

// Lógica de los mensajes del chat abierto, extraída del Dashboard.
//
// Los mensajes no tienen un estado propio: viven dentro de `selectedChat.messages`
// (y se cachean en `chats[].messages`). Por eso este hook NO es dueño de
// `selectedChat`: lo recibe del contenedor (que lo comparte con los chats, el
// historial del navegador, el CSS móvil y el WebSocket) y lo lee/escribe a través
// de `selectedChat` / `setSelectedChat`. Igual con `selectedChatRef` (lo leen el
// popstate y el WebSocket, que siguen en el contenedor) y con `markChatAsRead`
// (lo llama también el callback WS, así que se queda en el contenedor).
//
// El hook sí es dueño del texto del input, del estado de atención (capturar/cerrar)
// y de la paginación de mensajes, y expone `handleChatSelect` (puente lista→panel)
// y `handleSendMessage` para el contenedor.
export function useMessages({
  selectedChat,
  setSelectedChat,
  selectedChatRef,
  selectedBotId,
  setChats,
  reloadChatsFromBackend,
  markChatAsRead,
}) {
  const [message, setMessage] = useState("");
  // Estado de atención del chat seleccionado en el chat en vivo (captura/cierre).
  const [supportStatus, setSupportStatus] = useState(null);
  const [supportActionLoading, setSupportActionLoading] = useState(false);
  // Rechazo del backend al enviar, atado al chat donde ocurrió para no arrastrarlo a otro.
  const [sendError, setSendError] = useState(null);
  const [enviandoMedia, setEnviandoMedia] = useState(false);
  // El estado no frena dos clics en el mismo tick: el ref sí.
  const enviandoMediaRef = useRef(false);

  const sendErrorText = sendError && sendError.chatId === selectedChat?.chatId ? sendError.text : null;

  const messagesContainerRef = useRef(null);
  const chatAbiertoId = selectedChat ? selectedChat.chatId : null;

  // Ventana de 24 h de WhatsApp: cuenta también los entrantes ya pintados, que
  // pueden ser más nuevos que el `lastIncomingMessageTime` del listado. El cierre
  // se recalcula solo cuando cambian sus datos (no con cada tecla) y el hook
  // programa un render para cuando se cumplan las 24 h con el chat abierto.
  const plataformaChat = selectedChat?.platform;
  const ultimoEntranteListado = selectedChat?.lastIncomingMessageTime;
  const mensajesChat = selectedChat?.messages;
  const cierreVentana = useMemo(() => cierreVentanaWhatsApp(
    { platform: plataformaChat, lastIncomingMessageTime: ultimoEntranteListado },
    (mensajesChat || [])
      .filter(m => m.sender === 'client')
      .map(m => m.timestamp || m.createdAt)
  ), [plataformaChat, ultimoEntranteListado, mensajesChat]);
  const ventanaCerrada = useVentanaCerrada(cierreVentana, chatAbiertoId);
  const { pegadoAbajo, marcarPegado } = usePegadoAbajo(messagesContainerRef, chatAbiertoId);
  const messageInputRef = useRef(null);

  // Estado de paginación para mensajes del chat seleccionado
  const [messagesPagination, setMessagesPagination] = useState({
    currentPage: 0,
    pageSize: 50,
    totalElements: 0,
    totalPages: 0,
    hasMore: false,
    isLoading: false,
    isInitialLoad: true
  });

  // Referencia para mantener la posición del scroll al cargar mensajes antiguos
  const scrollPositionRef = useRef(null);
  const previousScrollHeightRef = useRef(0);

  // Referencia para rastrear el último mensaje al final (para detectar mensajes nuevos vs antiguos)
  const lastMessageIdRef = useRef(null);

  const handleSendMessage = async (buttonText = null) => {
    // Si se pasa texto del botón, usarlo; si no, usar el estado message
    const messageText = String(buttonText || message || '');
    if (!messageText.trim() || !selectedChat || ventanaCerrada) return;
    setSendError(null);

    const tempId = `temp_${Date.now()}`;
    const now = new Date();
    // ✅ Obtener el username del usuario desde localStorage
    const username = localStorage.getItem("username") || null;
    const newMessage = {
      id: tempId,
      text: messageText,
      sender: "agent",
      time: now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      timestamp: now.toISOString(), // ✅ Agregar timestamp ISO para ordenamiento correcto
      createdAt: now.toISOString(), // ✅ Agregar createdAt para ordenamiento correcto
      dbId: Date.now(), // ✅ Usar timestamp como dbId temporal para desempate
      isTemporary: true, // Marcar como temporal
      senderUsername: username // ✅ Incluir username del usuario que envía el mensaje
    };

    // Actualizar el chat seleccionado inmediatamente (optimistic update)
    setSelectedChat(prevSelectedChat => ({
      ...prevSelectedChat,
      messages: [...(prevSelectedChat.messages || []), newMessage],
      lastMessage: messageText,
      unread: 0,
      avatar: prevSelectedChat.platform === 'telegram' ? 'TG' : prevSelectedChat.platform === 'instagram' ? 'IG' : 'WA'
    }));
    
    // Hacer scroll al final después de agregar el mensaje
    // Usar doble requestAnimationFrame para asegurar que el DOM esté completamente actualizado
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    });

    // FASE 2: Actualizar la lista de chats con ordenamiento
    setChats(prevChats => {
      const updatedChats = prevChats.map(chat => 
        chat.chatId === selectedChat.chatId 
          ? { 
              ...chat, 
              messages: [...(chat.messages || []), newMessage],
              lastMessage: messageText,
              unread: 0,
              avatar: chat.platform === 'telegram' ? 'TG' : chat.platform === 'instagram' ? 'IG' : 'WA'
            }
          : chat
      );
      
      // Recargar chats desde el backend para mantener el orden correcto
      // Esto garantiza que el orden siempre sea correcto según el backend
      reloadChatsFromBackend(true); // preserveMessages = true para mantener mensajes ya cargados
      
      return updatedChats;
    });

    const messageToSend = messageText; // Guardar el mensaje antes de limpiarlo
    if (!buttonText) {
      setMessage(""); // Solo limpiar si no viene de un botón
    }

    // Mantener el foco en el textarea y asegurar que esté visible encima del teclado
    // Usar requestAnimationFrame para asegurar que el DOM se haya actualizado
    requestAnimationFrame(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
        // Hacer scroll al textarea para mantenerlo visible encima del teclado
        messageInputRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end',
          inline: 'nearest'
        });
      }
    });

    // ✅ ENVIAR AL BACKEND PARA GUARDAR EN BD Y PLATAFORMA CORRESPONDIENTE
    try {
      console.log('🔍 Debug - Chat ID:', selectedChat.id);
      console.log('🔍 Debug - Selected Chat:', selectedChat);
      console.log('🔍 Debug - Platform:', selectedChat.platform);
      
      // Usar chatId del DTO unificado
      const chatIdString = selectedChat.chatId.toString();
      console.log('🔍 Debug - Chat ID as String:', chatIdString);
      
      // Determinar el endpoint según la plataforma
      const endpoint = selectedChat.platform === 'telegram' 
        ? getApiUrl(`/telegram/chats/${chatIdString}/send`)
        : selectedChat.platform === 'instagram'
        ? getApiUrl(`/instagram/chats/${chatIdString}/messages`)
        : getApiUrl(`/whatsapp/chats/${chatIdString}/messages`);
      
      console.log('🔍 Debug - Endpoint:', endpoint);
      
      // ✅ CRÍTICO: Incluir botId en el request para identificar el bot correcto
      const requestBody = {
        text: messageToSend
      };
      
      // Si el chat tiene botId, incluirlo en el request
      if (selectedChat.botId) {
        requestBody.botId = selectedChat.botId;
      }
      
      const response = await apiRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.status === 409) {
        // No salió: se quita el optimista, se devuelve el texto al input y se
        // enseña el motivo. Solo el code de ventana es «ventana cerrada».
        const data = await response.json().catch(() => null);
        const sinTemporal = msgs => (msgs || []).filter(m => m.id !== tempId);
        setSelectedChat(prev => (prev ? { ...prev, messages: sinTemporal(prev.messages) } : prev));
        setChats(prev => prev.map(chat =>
          chat.chatId === selectedChat.chatId ? { ...chat, messages: sinTemporal(chat.messages) } : chat
        ));
        setMessage(actual => actual || messageToSend);
        const texto = codigoDelError(data) === CODIGOS.VENTANA
          ? textoDelError(data) ?? AVISO_VENTANA_CERRADA
          : motivoDelEnvio(409, data);
        setSendError({ chatId: selectedChat.chatId, text: texto });
        return;
      }

      if (!response.ok) {
        throw new Error('Error al enviar mensaje');
      }

      const result = await response.json();
      console.log('✅ Mensaje enviado exitosamente:', result);
      
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);
      // Opcional: Revertir el estado si falla
      // setSelectedChat(prevSelectedChat => ({
      //   ...prevSelectedChat,
      //   messages: prevSelectedChat.messages.filter(msg => msg.id !== newMessage.id)
      // }));
    }
  };

  // Sin mensaje optimista: el mensaje llega por WebSocket cuando el backend lo guarda.
  const handleSendMedia = useCallback(async (archivo, { caption = '', voice = false, idempotencyKey } = {}) => {
    if (!selectedChat || selectedChat.internalChatId == null || selectedChat.platform === 'telegram'
        || ventanaCerrada || enviandoMediaRef.current) {
      return false;
    }
    const chatId = selectedChat.chatId;
    enviandoMediaRef.current = true;
    setEnviandoMedia(true);
    setSendError(null);
    try {
      const form = new FormData();
      form.append('archivo', archivo);
      if (caption.trim()) form.append('caption', caption);
      if (voice) form.append('voice', 'true');
      const res = await apiRequest(getApiUrl(`/chats/${selectedChat.internalChatId}/media`), {
        method: 'POST',
        body: form,
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (res.ok) return true;
      const data = await res.json().catch(() => null);
      setSendError({ chatId, text: motivoDelEnvio(res.status, data) });
      return false;
    } catch (error) {
      setSendError({ chatId, text: 'Error de red al enviar el archivo' });
      return false;
    } finally {
      enviandoMediaRef.current = false;
      setEnviandoMedia(false);
    }
  }, [selectedChat, ventanaCerrada]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Función para cargar mensajes con paginación
  const loadMessages = async (chatId, platform, page = 0, size = 50, botId = null) => {
    try {
      setMessagesPagination(prev => ({ ...prev, isLoading: true }));
      
      // ✅ CRÍTICO: Incluir botId en la URL para filtrar mensajes por bot
      let endpoint = '';
      if (platform === 'telegram') {
        endpoint = getApiUrl(`/telegram/chats/${chatId}/messages?page=${page}&size=${size}`);
        if (botId) {
          endpoint += `&botId=${botId}`;
        }
      } else if (platform === 'instagram') {
        endpoint = getApiUrl(`/instagram/chats/${chatId}/messages?page=${page}&size=${size}`);
        if (botId) {
          endpoint += `&botId=${botId}`;
        }
      } else {
        endpoint = getApiUrl(`/whatsapp/chats/${chatId}/messages?page=${page}&size=${size}`);
        if (botId) {
          endpoint += `&botId=${botId}`;
        }
      }
      
      console.log(`📡 Cargando mensajes - Página ${page}, Tamaño ${size}, BotId: ${botId}: ${endpoint}`);

      const response = await apiRequest(endpoint);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Manejar respuesta PagedResponse del backend
      const pagedData = data.content ? data : { content: data.messages || data, ...data };
      const messages = pagedData.content || [];
      
      console.log(`✅ Mensajes cargados: ${messages.length} de ${pagedData.totalElements || messages.length} totales`);
      
      // Convertir mensajes al formato esperado por el frontend
      const formattedMessages = messages.map(msg => ({
        id: msg.messageId || msg.id,
        dbId: msg.dbId || msg.id,
        text: msg.text,
        sender: msg.isFromBot ? "agent" : "client",
        time: msg.timestamp || msg.createdAt,
        timestamp: msg.timestamp,
        createdAt: msg.createdAt,
        platform: msg.platform || platform,
        buttons: msg.buttons || null, // Agregar botones si existen
        senderUsername: msg.senderUsername || null, // ✅ Incluir username del usuario que envió el mensaje
        deliveryStatus: msg.deliveryStatus || null,
        deliveryErrorCode: msg.deliveryErrorCode ?? null,
        deliveryErrorMessage: msg.deliveryErrorMessage ?? null,
        mediaFileId: msg.mediaFileId || null,
        mediaEstado: msg.mediaEstado || null,
        messageType: msg.messageType || null,
        fileName: msg.fileName || null,
        mimeType: msg.mimeType || null,
        fileSize: msg.fileSize || null,
        mediaAntigua: !!msg.mediaAntigua
      }));
      
      // Actualizar estado de paginación
      setMessagesPagination({
        currentPage: pagedData.page || page,
        pageSize: pagedData.size || size,
        totalElements: pagedData.totalElements || messages.length,
        totalPages: pagedData.totalPages || Math.ceil((pagedData.totalElements || messages.length) / size),
        hasMore: pagedData.hasNext !== undefined ? pagedData.hasNext : (page < (pagedData.totalPages || 1) - 1),
        isLoading: false,
        isInitialLoad: page === 0
      });
      
      // Ascendentes, como espera el resto del hook (ver utils/ordenMensajes).
      return ordenarMensajes(formattedMessages);
    } catch (error) {
      console.error('❌ Error cargando mensajes:', error);
      setMessagesPagination(prev => ({ ...prev, isLoading: false }));
      return [];
    }
  };
  
  // Función para cargar más mensajes antiguos (scroll infinito hacia arriba)
  const loadMoreMessages = useCallback(async () => {
    if (!selectedChat || messagesPagination.isLoading || !messagesPagination.hasMore) {
      return;
    }
    
    const nextPage = messagesPagination.currentPage + 1;
    console.log(`📜 Cargando página ${nextPage} de mensajes antiguos...`);
    
    // Guardar posición actual del scroll antes de cargar
    const container = messagesContainerRef.current;
    if (container) {
      previousScrollHeightRef.current = container.scrollHeight;
      scrollPositionRef.current = container.scrollTop;
    }
    
    // ✅ CRÍTICO: Pasar botId al cargar más mensajes para filtrar correctamente
    const chatBotId = selectedChat.botId || selectedBotId;
    
    // Cargar mensajes de la siguiente página
    const newMessages = await loadMessages(
      selectedChat.chatId,
      selectedChat.platform,
      nextPage,
      messagesPagination.pageSize,
      chatBotId
    );
    
    if (newMessages.length > 0) {
      // Insertar mensajes antiguos al principio del array
      setSelectedChat(prevChat => {
        if (!prevChat) return prevChat;
        
        // Evitar duplicados
        const existingIds = new Set(prevChat.messages?.map(m => m.id) || []);
        const uniqueNewMessages = newMessages.filter(m => !existingIds.has(m.id));
        
        return {
          ...prevChat,
          messages: [...uniqueNewMessages, ...(prevChat.messages || [])],
          avatar: prevChat.platform === 'telegram' ? 'TG' : prevChat.platform === 'instagram' ? 'IG' : 'WA'
        };
      });
      
      // Restaurar posición del scroll después de que el DOM se actualice
      // Usar doble requestAnimationFrame para asegurar que el DOM esté completamente renderizado
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container && scrollPositionRef.current !== null && previousScrollHeightRef.current > 0) {
            const newScrollHeight = container.scrollHeight;
            const heightDifference = newScrollHeight - previousScrollHeightRef.current;
            container.scrollTop = scrollPositionRef.current + heightDifference;
            console.log(`✅ Posición del scroll restaurada. Diferencia de altura: ${heightDifference}px`);
          }
        });
      });
    }
  }, [selectedChat, selectedBotId, messagesPagination.isLoading, messagesPagination.hasMore, messagesPagination.currentPage, messagesPagination.pageSize]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // El listener de abajo llama a la versión al día de loadMoreMessages a través
  // de esta ref: así no hay que re-registrarlo cada vez que cambia selectedChat
  // (cada tick o evento de archivo crea un objeto nuevo), solo al cambiar de chat
  // o de estado de paginación.
  const loadMoreMessagesRef = useRef(loadMoreMessages);
  useEffect(() => {
    loadMoreMessagesRef.current = loadMoreMessages;
  }, [loadMoreMessages]);

  // Hook para detectar scroll cerca del inicio y cargar más mensajes
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || chatAbiertoId == null || !messagesPagination.hasMore || messagesPagination.isLoading) {
      return;
    }
    
    let scrollTimeout;
    const handleScroll = () => {
      // Limpiar timeout anterior
      clearTimeout(scrollTimeout);
      
      // Debounce: esperar 150ms después del último scroll
      scrollTimeout = setTimeout(() => {
        const scrollTop = container.scrollTop;
        const scrollThreshold = 300; // Cargar cuando esté a 300px del inicio
        
        // Si está cerca del inicio y hay más mensajes, cargar
        if (scrollTop < scrollThreshold && messagesPagination.hasMore && !messagesPagination.isLoading) {
          console.log(`📜 Scroll detectado cerca del inicio (${scrollTop}px), cargando más mensajes...`);
          loadMoreMessagesRef.current();
        }
      }, 150);
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [chatAbiertoId, messagesPagination.hasMore, messagesPagination.isLoading]);
  
  const handleChatSelect = async (chat) => {
    console.log('✅ Chat seleccionado:', chat);
    
    // ✅ CRÍTICO: Validar que el chat pertenece al bot seleccionado (comparación estricta)
    if (selectedBotId && chat.botId && Number(chat.botId) !== Number(selectedBotId)) {
      console.warn(`⚠️ El chat no pertenece al bot seleccionado - Chat botId: ${chat.botId}, Selected: ${selectedBotId}`);
      return;
    }

    const chatBotId = chat.botId || selectedBotId;
    const avatar = chat.platform === 'telegram' ? 'TG' : chat.platform === 'instagram' ? 'IG' : 'WA';

    // ¿Es el mismo chat que ya está abierto? (evita re-render/parpadeo y entradas
    // de historial duplicadas al volver a pulsar la conversación activa)
    const isSameChat = selectedChatRef.current
      && String(selectedChatRef.current.chatId) === String(chat.chatId)
      && (!selectedChatRef.current.botId || !chat.botId
        || Number(selectedChatRef.current.botId) === Number(chat.botId));
    if (isSameChat) return;

    // Agregar estado al historial para que el botón de atrás funcione correctamente
    window.history.pushState({ view: 'chat', chatId: chat.chatId }, '', window.location.pathname);

    // Resetear referencia del último mensaje al cambiar de chat
    lastMessageIdRef.current = null;

    // 1) Mostrar la conversación INMEDIATAMENTE, sin esperar a la red. Así el
    //    cambio es instantáneo y no hay "aparece / desaparece / aparece": el
    //    panel se pinta una sola vez y los mensajes se rellenan después.
    setSelectedChat({
      ...chat,
      messages: Array.isArray(chat.messages) ? chat.messages : [],
      avatar
    });
    setMessagesPagination({
      currentPage: 0,
      pageSize: 50,
      totalElements: 0,
      totalPages: 0,
      hasMore: false,
      isLoading: true,
      isInitialLoad: true
    });
    // 🎧 Ocultar acciones de atención hasta conocer el estado del nuevo chat.
    setSupportStatus(null);

    // 2) Consultar el estado de atención (botón Capturar/Cerrar) EN PARALELO con
    //    la carga de mensajes. Antes se lanzaba tras `await loadMessages`, lo que
    //    encadenaba dos viajes de red y hacía que el botón tardase en aparecer.
    markChatAsRead(chat.chatId, chat.platform, chatBotId);
    fetchSupportStatus(chat.chatId, chatBotId);

    // 3) Cargar mensajes en segundo plano y fusionar sólo si seguimos en el
    //    mismo chat (evita que una respuesta tardía pise otra conversación).
    const loadedMessages = await loadMessages(chat.chatId, chat.platform, 0, 50, chatBotId);
    setSelectedChat(prevChat => {
      if (!prevChat || String(prevChat.chatId) !== String(chat.chatId)) {
        return prevChat;
      }
      return { ...prevChat, messages: loadedMessages, avatar };
    });

    // Cachear los mensajes en la lista para que reabrir el chat sea instantáneo
    // (evita el "vacío tras el slide"). El sondeo/WS mantiene la caché fresca y,
    // al reabrir, igualmente se refresca en segundo plano.
    setChats(prevChats => prevChats.map(c => {
      const idMatches = String(c.chatId) === String(chat.chatId);
      const botMatches = !chatBotId || !c.botId || Number(c.botId) === Number(chatBotId);
      return idMatches && botMatches ? { ...c, messages: loadedMessages } : c;
    }));

    // Scroll al final una vez pintados los mensajes.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom());
    });
  };

  // 🎧 ===== Atención al cliente (desde el chat en vivo) =====

  // Consulta si el chat está capturado y si el usuario puede capturarlo.
  const fetchSupportStatus = useCallback(async (chatId, botId) => {
    if (!chatId || !botId) return;
    try {
      const res = await apiRequest(getApiUrl(`/support/status?chatId=${encodeURIComponent(chatId)}&botId=${botId}`));
      if (!res.ok) return;
      const data = await res.json();
      // Evitar aplicar la respuesta si el usuario ya cambió de chat.
      setSelectedChat(prev => {
        if (prev && String(prev.chatId) === String(chatId)) {
          setSupportStatus(data);
        }
        return prev;
      });
    } catch (_) {
      // silencioso: sin estado no se muestra el botón
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincroniza el badge de atención de la cabecera en tiempo real cuando otro
  // agente/supervisor captura o cierra la MISMA conversación que tenemos abierta.
  // Push por WebSocket (topic /support por tenant); solo hace un GET puntual si el
  // evento afecta al chat abierto, así que no penaliza la escalabilidad.
  useEffect(() => {
    const onSupportEvent = (e) => {
      const ev = e.detail;
      if (!ev) return;
      const sel = selectedChatRef.current;
      if (sel && ev.chatId != null && String(sel.chatId) === String(ev.chatId)) {
        fetchSupportStatus(ev.chatId, ev.botId);
      }
    };
    window.addEventListener('support-event', onSupportEvent);
    return () => window.removeEventListener('support-event', onSupportEvent);
  }, [fetchSupportStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Captura la conversación abierta y la lleva a la bandeja del agente.
  const handleCaptureConversation = useCallback(async () => {
    if (!selectedChat || supportActionLoading) return;
    const botId = selectedChat.botId || selectedBotId;
    setSupportActionLoading(true);
    try {
      const res = await apiRequest(getApiUrl('/support/sessions'), {
        method: 'POST',
        body: JSON.stringify({ chatId: selectedChat.chatId, botId }),
      });
      if (res.ok) {
        await fetchSupportStatus(selectedChat.chatId, botId);
      } else {
        // 409: ya capturada por otro agente → refrescar estado real.
        await fetchSupportStatus(selectedChat.chatId, botId);
      }
    } catch (_) {
      // silencioso
    } finally {
      setSupportActionLoading(false);
    }
  }, [selectedChat, selectedBotId, supportActionLoading, fetchSupportStatus]);

  // Cierra la conversación capturada (desde el chat en vivo, supervisión).
  const handleCloseConversation = useCallback(async () => {
    if (!selectedChat || supportActionLoading) return;
    const botId = selectedChat.botId || selectedBotId;
    setSupportActionLoading(true);
    try {
      const res = await apiRequest(getApiUrl('/support/close-by-chat'), {
        method: 'POST',
        body: JSON.stringify({ chatId: selectedChat.chatId, botId }),
      });
      if (res.ok || res.status === 204) {
        await fetchSupportStatus(selectedChat.chatId, botId);
      }
    } catch (_) {
      // silencioso
    } finally {
      setSupportActionLoading(false);
    }
  }, [selectedChat, selectedBotId, supportActionLoading, fetchSupportStatus]);

  // Función para hacer scroll al último mensaje
  const scrollToBottom = useCallback(() => {
    marcarPegado();
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      // Usar scrollTo con behavior smooth para una animación suave
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [marcarPegado]);

  // Una imagen que termina de cargar crece y deja la vista corta: si el usuario
  // seguía leyendo lo último, se vuelve a bajar; si había subido, no se le mueve.
  const alCargarMedia = useCallback(() => {
    if (pegadoAbajo()) scrollToBottom();
  }, [pegadoAbajo, scrollToBottom]);

  // Efecto optimizado para scroll automático (solo en carga inicial)
  useEffect(() => {
    if (!selectedChat || !messagesContainerRef.current) return;

    // Solo hacer scroll automático si es la carga inicial
    if (selectedChat.messages && selectedChat.messages.length > 0 && messagesPagination.isInitialLoad) {
      // Guardar el último mensaje en la referencia para futuras comparaciones
      const messages = selectedChat.messages;
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.id) {
        lastMessageIdRef.current = lastMessage.id;
      }
      
      // Usar requestAnimationFrame para mejor rendimiento
      const scrollTimer = requestAnimationFrame(() => {
        scrollToBottom();
        // Marcar que ya no es carga inicial
        setMessagesPagination(prev => ({ ...prev, isInitialLoad: false }));
      });
      
      return () => cancelAnimationFrame(scrollTimer);
    }
  }, [selectedChat, messagesPagination.isInitialLoad, scrollToBottom]);

  // Efecto específico para detectar nuevos mensajes por WebSocket
  useEffect(() => {
    if (!selectedChat || !messagesContainerRef.current || !selectedChat.messages || selectedChat.messages.length === 0) {
      // Resetear referencia cuando no hay mensajes
      lastMessageIdRef.current = null;
      return;
    }

    // Obtener el último mensaje del array (mensajes más recientes están al final)
    const messages = selectedChat.messages;
    const lastMessage = messages[messages.length - 1];
    const currentLastMessageId = lastMessage?.id;

    // Solo hacer scroll automático si el último mensaje es realmente nuevo (diferente al anterior)
    // Esto evita hacer scroll cuando se cargan mensajes antiguos al principio del array
    if (currentLastMessageId && currentLastMessageId !== lastMessageIdRef.current) {
      // Actualizar referencia al nuevo último mensaje
      lastMessageIdRef.current = currentLastMessageId;
      
      // Hacer scroll automático solo para mensajes nuevos al final
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
    } else if (!lastMessageIdRef.current) {
      // Si no hay referencia previa, es la carga inicial, guardar el último mensaje
      lastMessageIdRef.current = currentLastMessageId;
    }
  }, [selectedChat, scrollToBottom]);

  // Mantener el textarea visible encima del teclado cuando se selecciona un chat.
  // Depende de la clave del chat y no del objeto: un tick, un evento de archivo o
  // un mensaje nuevo crean un selectedChat nuevo y no deben mover la vista.
  useEffect(() => {
    if (chatAbiertoId != null && messageInputRef.current) {
      // Pequeño delay para asegurar que el DOM esté completamente renderizado
      const timer = setTimeout(() => {
        if (messageInputRef.current) {
          messageInputRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'end',
            inline: 'nearest'
          });
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [chatAbiertoId]); // Solo cuando cambia el chat seleccionado

  return {
    message,
    setMessage,
    handleSendMessage,
    handleSendMedia,
    enviandoMedia,
    handleKeyPress,
    messageInputRef,
    messagesContainerRef,
    messagesPagination,
    loadMoreMessages,
    supportStatus,
    supportActionLoading,
    handleCaptureConversation,
    handleCloseConversation,
    handleChatSelect,
    ventanaCerrada,
    sendErrorText,
    alCargarMedia,
  };
}

export default useMessages;
